
/* Implements Warm Academic theme styling for footer */
import React from 'react';
import { 
  ShieldCheck, Globe, Lock, ExternalLink, Activity, 
  Cpu, ShieldAlert, Zap, Server, Target, MessageSquare, HelpCircle,
  BookOpen, Users, PhoneCall, Award, FileText
} from 'lucide-react';
import { useAuth } from './AuthBridge';
import { AdConfig } from '../types';
import { Link } from 'react-router-dom';
import { AdUnit } from './AdUnit';
import { SponsoredService } from '../services/sponsoredService';

interface FooterProps {
  config?: AdConfig;
}

export const DIAMOND_NAV_LINKS = [
  { name: 'Book CNA Class', url: 'https://www.diamondnursingschool.com/cna-career', icon: BookOpen },
  { name: 'CNA Class Group', url: 'https://www.diamondnursingschool.com/group/cna-class-group/discussion', icon: Users },
  { name: 'Contact Us', url: 'https://www.diamondnursingschool.com/contact', icon: PhoneCall },
  { name: 'Online CEU', url: 'https://www.diamondnursingschool.com/cna-ceu-online', icon: Award },
  { name: 'Terms and Conditions', url: 'https://www.diamondnursingschool.com/compliance/terms-and-conditions', icon: FileText }
];

export const SOCIAL_LINKS = [
  {
    name: 'Instagram',
    url: 'https://www.instagram.com/diamondnursingschool/',
    bgClass: 'bg-gradient-to-tr from-amber-500 via-rose-500 to-purple-600 hover:opacity-90',
    icon: (
      <svg className="w-4 h-4 md:w-5 md:h-5 fill-current text-white" viewBox="0 0 24 24">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
      </svg>
    )
  },
  {
    name: 'Facebook',
    url: 'https://www.facebook.com/people/Diamond-School-Of-Nursing/61568499564734/',
    bgClass: 'bg-[#1877F2] hover:bg-[#166fe5]',
    icon: (
      <svg className="w-4 h-4 md:w-5 md:h-5 fill-current text-white" viewBox="0 0 24 24">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    )
  },
  {
    name: 'LinkedIn',
    url: 'https://www.linkedin.com/company/diamond-school-of-nursing/',
    bgClass: 'bg-[#0A66C2] hover:bg-[#09519a]',
    icon: (
      <svg className="w-4 h-4 md:w-5 md:h-5 fill-current text-white" viewBox="0 0 24 24">
        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
      </svg>
    )
  },
  {
    name: 'YouTube',
    url: 'https://www.youtube.com/@DiamondSchoolOfNursing',
    bgClass: 'bg-[#FF0000] hover:bg-[#e60000]',
    icon: (
      <svg className="w-4 h-4 md:w-5 md:h-5 fill-current text-white" viewBox="0 0 24 24">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>
    )
  },
  {
    name: 'TikTok',
    url: 'https://www.tiktok.com/@diamondnursingschool',
    bgClass: 'bg-[#000000] hover:bg-slate-900 border border-slate-700',
    icon: (
      <svg className="w-4 h-4 md:w-5 md:h-5 fill-current text-white" viewBox="0 0 24 24">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.24-2.38.71-4.77 2.45-6.38 1.74-1.6 4.16-2.43 6.54-2.14v4.29c-.83-.17-1.71-.05-2.47.33-.76.38-1.37 1.05-1.66 1.86-.31.84-.21 1.79.25 2.55.45.76 1.25 1.27 2.12 1.38.87.11 1.75-.18 2.37-.8.56-.56.88-1.34.88-2.14V.02z"/>
      </svg>
    )
  }
];

const SocialFollowBlock: React.FC<{ compact?: boolean }> = ({ compact = false }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2">
      <span className="text-[9px] font-black uppercase tracking-[0.25em] text-slate-300">
        Follow Us
      </span>
      <div className="h-0.5 w-8 bg-indigo-500/60 rounded-full" />
    </div>
    <div className="flex items-center gap-2 md:gap-2.5 flex-wrap">
      {SOCIAL_LINKS.map((s) => (
        <a
          key={s.name}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          title={`Follow us on ${s.name} (opens in new tab)`}
          aria-label={`Follow Diamond School of Nursing on ${s.name} (opens in new tab)`}
          className={`${compact ? 'w-8 h-8' : 'w-9 h-9 md:w-10 md:h-10'} rounded-xl flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-md ${s.bgClass}`}
        >
          {s.icon}
        </a>
      ))}
    </div>
  </div>
);

export const Footer: React.FC<FooterProps> = ({ config }) => {
  const { user, activeCert } = useAuth();
  const currentYear = new Date().getFullYear();
  const certId = activeCert?.id || user?.activeCertId || 'cna';
  const profilePath = `/${certId}/profile`;
  const safeConfig = config || SponsoredService.getConfig();
  const isClinicalTheme = safeConfig.activeTheme === 'clinical_trust';
  const isWarmAcademic = safeConfig.activeTheme === 'warm_academic';

  const Disclaimer = () => (
    <div className="flex items-start gap-4">
      <ShieldAlert size={12} className={isClinicalTheme || isWarmAcademic ? "text-amber-400 shrink-0 mt-0.5" : "text-slate-700 shrink-0 mt-0.5"} />
      <p className={`text-[7px] md:text-[8px] font-bold tracking-widest leading-relaxed text-left ${isClinicalTheme || isWarmAcademic ? 'text-slate-200' : 'text-slate-600'}`}>
        DEVELOPED BY EDUCATORS: ALL STUDY GUIDES, QUESTIONS, AUDIOS, AND PROCEDURES IN THIS HUB ARE SCIENTIFICALLY FORMULATED AND PEER-REVIEWED BY LICENSED NURSING EDUCATORS. PROCEDURES MUST ALSO BE VERIFIED AGAINST YOUR SPECIFIC STATE BOARD HANDBOOK.
      </p>
    </div>
  );

  // --- MINI FOOTER VARIANT ---
  if (safeConfig.footerType === 'mini') {
    return (
      <footer className={`w-full border-t print:hidden shrink-0 relative z-10 px-4 sm:px-6 py-6 ${isWarmAcademic ? 'bg-[#0B5C5C] border-[#0A5252] text-white' : isClinicalTheme ? 'bg-[#0B2942] border-slate-800 text-white' : 'bg-slate-950 border-slate-900 text-white'}`}>
        <div className="max-w-7xl mx-auto space-y-5">
          {/* Mobile Footer Banner */}
          <div className="w-full md:hidden mb-2">
            <AdUnit placement="footer_mobile" />
          </div>

          {/* Top Row: Brand & Quick Links */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 border-b border-white/10 pb-5">
            <div className="space-y-3">
              <p className={`text-[10px] font-black tracking-[0.2em] ${isClinicalTheme ? 'text-slate-300' : 'text-slate-400'}`}>
                &copy; {currentYear} Diamond School Of Nursing
              </p>
              {/* Quick links list for mini footer */}
              <div className="flex flex-wrap items-center gap-3 md:gap-5">
                {DIAMOND_NAV_LINKS.map((link) => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-bold text-slate-200 hover:text-teal-300 transition-colors flex items-center gap-1 group"
                  >
                    <span>{link.name}</span>
                    <ExternalLink size={10} className="text-slate-400 group-hover:text-teal-300" aria-hidden="true" />
                  </a>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('open-cookie-preferences'));
                    }
                  }}
                  className="text-[10px] font-bold text-teal-300 hover:text-teal-200 transition-colors flex items-center gap-1 cursor-pointer"
                >
                  <ShieldCheck size={11} className="text-teal-400" />
                  <span>Cookie Choices</span>
                </button>
              </div>
            </div>

            {/* Social Media Follow Icons */}
            <SocialFollowBlock compact />
          </div>

          {/* Middle Row: System Security & Help */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-1.5">
                 <Lock size={12} className={isClinicalTheme ? "text-teal-400" : "text-emerald-500"} />
                 <span className={`text-[8px] font-black tracking-tighter ${isClinicalTheme ? 'text-teal-300' : 'text-slate-400'}`}>Verified Secure</span>
              </div>
              <Link to={profilePath + "#support"} className="flex items-center gap-1.5 group transition-colors">
                 <MessageSquare size={12} className={isClinicalTheme ? "text-teal-400 group-hover:text-teal-300" : "text-indigo-400 group-hover:text-indigo-300"} />
                 <span className={`text-[8px] font-black tracking-widest ${isClinicalTheme ? 'text-slate-300 group-hover:text-white' : 'text-slate-400 group-hover:text-slate-200'}`}>Help and feedback</span>
              </Link>
              <Link to={`/faq/${certId}`} className="flex items-center gap-1.5 group transition-colors">
                 <HelpCircle size={12} className={isClinicalTheme ? "text-teal-400 group-hover:text-teal-300" : "text-indigo-400 group-hover:text-indigo-300"} />
                 <span className={`text-[8px] font-black tracking-widest ${isClinicalTheme ? 'text-slate-300 group-hover:text-white' : 'text-slate-400 group-hover:text-slate-200'}`}>FAQ</span>
              </Link>
              <div className="flex items-center gap-1.5">
                 <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.4)]"></div>
                 <span className="text-[8px] font-black text-emerald-400 tracking-widest">Board Aligned</span>
              </div>
            </div>

            <div className="hidden lg:flex items-center gap-4 opacity-40">
                <div className="flex items-center gap-1.5">
                  <Cpu size={10} className="text-slate-400" />
                  <span className="text-[7px] font-black text-slate-300">Node: US-EAST</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Server size={10} className="text-slate-400" />
                  <span className="text-[7px] font-black text-slate-300">GA-8</span>
                </div>
            </div>
          </div>
          
          <div className={`pt-3 border-t ${isClinicalTheme ? 'border-slate-800' : 'border-slate-900'}`}>
            <Disclaimer />
          </div>
        </div>
      </footer>
    );
  }

  // --- STANDARD FOOTER VARIANT ---
  return (
    <footer className={`w-full border-t print:hidden shrink-0 relative z-10 ${isWarmAcademic ? 'bg-[#0B5C5C] border-[#0A5252] text-white' : isClinicalTheme ? 'bg-[#0B2942] border-slate-800 text-white' : 'bg-slate-950 border-slate-900 text-white'}`}>
      {/* Mobile Footer Banner */}
      <div className="w-full md:hidden max-w-7xl mx-auto px-4 sm:px-6 pt-6">
        <AdUnit placement="footer_mobile" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        
        {/* Brand & Social Section */}
        <div className="md:col-span-4 space-y-6">
          <div className="flex items-center gap-3">
             <button 
              onClick={() => window.location.href = '/'}
              className="focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-lg transition-all"
              aria-label="Return to homepage"
             >
               <img 
                src="https://static.wixstatic.com/media/cc4206_4525d5f9d45948bd8501990ac76f6c06~mv2.png" 
                className="h-7 md:h-9 brightness-0 invert opacity-80 hover:opacity-100 transition-all" 
                alt="Diamond School Of Nursing Logo"
              />
             </button>
            <div className={`h-6 w-px ${isClinicalTheme ? 'bg-slate-700' : 'bg-slate-800'}`}></div>
            <div className="flex flex-col">
              <span className={`text-[10px] font-black tracking-[0.2em] leading-none ${isClinicalTheme ? 'text-teal-300 font-serif' : 'text-slate-300'}`}>
                {isClinicalTheme ? 'Diamond School of Nursing' : 'Study Hub'}
              </span>
              <span className="text-[8px] font-bold tracking-widest text-amber-300 mt-1">Board Certified Standard</span>
            </div>
          </div>
          <p className="text-[11px] text-slate-300 font-medium leading-relaxed max-w-xs">
            "Providing premium, evidence-based clinical education developed and vetted by board-certified RN educators to guarantee official state-board readiness."
          </p>

          {/* Social Follow Block */}
          <div className="pt-2">
            <SocialFollowBlock />
          </div>

          <div className="flex items-center gap-3 pt-1">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${isClinicalTheme ? 'bg-[#0F7D6C]/30 border-teal-600/50' : 'bg-slate-900 border-slate-800/50'}`}>
               <ShieldCheck size={12} className="text-emerald-400" aria-hidden="true" />
               <span className="text-[8px] font-black text-teal-200 tracking-widest">Logic Verified</span>
            </div>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${isClinicalTheme ? 'bg-[#0F7D6C]/30 border-teal-600/50' : 'bg-slate-900 border-slate-800/50'}`}>
               <Activity size={12} className="text-teal-300" aria-hidden="true" />
               <span className="text-[8px] font-black text-teal-200 tracking-widest">State Board Ready</span>
            </div>
          </div>
        </div>

        {/* Links Navigation Grid */}
        <div className="md:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h4 className={`text-[9px] font-black tracking-[0.3em] uppercase border-b pb-2 ${isClinicalTheme ? 'text-teal-300 border-slate-800' : 'text-slate-400 border-slate-800'}`}>
              Institutional & Courses
            </h4>
            <nav aria-label="Institutional Links">
              <ul className="space-y-2.5">
                {DIAMOND_NAV_LINKS.slice(0, 4).map((link) => (
                  <li key={link.name}>
                    <a 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[11px] font-bold text-slate-200 hover:text-teal-300 transition-all flex items-center gap-2 group"
                    >
                      <link.icon size={12} className="text-indigo-400 group-hover:text-teal-300" />
                      <span>{link.name}</span>
                      <ExternalLink size={10} className="text-slate-500 group-hover:text-teal-300 ml-auto" aria-hidden="true" />
                    </a>
                  </li>
                ))}
                <li>
                  <Link to={`/${certId}/help`} className="text-[10px] font-semibold text-slate-300 hover:text-white transition-all flex items-center gap-2 group pt-1">
                    <span>Study Hub Manual</span>
                    <ExternalLink size={10} className="text-slate-500 group-hover:text-teal-300" aria-hidden="true" />
                  </Link>
                </li>
                <li>
                  <Link to={profilePath + "#support"} className="text-[10px] font-semibold text-slate-300 hover:text-white transition-all flex items-center gap-2 group">
                    <span>Help & Feedback</span>
                    <MessageSquare size={10} className="text-slate-500 group-hover:text-teal-300" aria-hidden="true" />
                  </Link>
                </li>
                <li>
                  <Link to={`/faq/${certId}`} className="text-[10px] font-semibold text-slate-300 hover:text-white transition-all flex items-center gap-2 group">
                    <span>Frequently Asked Questions</span>
                    <HelpCircle size={10} className="text-slate-500 group-hover:text-teal-300" aria-hidden="true" />
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          <div className="space-y-4">
            <h4 className={`text-[9px] font-black tracking-[0.3em] uppercase border-b pb-2 ${isClinicalTheme ? 'text-teal-300 border-slate-800' : 'text-slate-400 border-slate-800'}`}>
              Governance & Policies
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a 
                  href="https://www.diamondnursingschool.com/compliance/terms-and-conditions" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[11px] font-bold text-slate-200 hover:text-teal-300 transition-all flex items-center gap-2 group"
                >
                  <FileText size={12} className="text-indigo-400 group-hover:text-teal-300" />
                  <span>Terms and Conditions</span>
                  <ExternalLink size={10} className="text-slate-500 group-hover:text-teal-300 ml-auto" aria-hidden="true" />
                </a>
              </li>
              <li>
                <a href="https://www.diamondnursingschool.com/compliance/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-slate-300 hover:text-white transition-all flex items-center gap-1.5">
                  Privacy Policy <ExternalLink size={9} className="text-slate-500" />
                </a>
              </li>
              <li>
                <Link to="/cookie-consent" className="text-[10px] font-semibold text-slate-300 hover:text-white transition-all flex items-center gap-1.5">
                  Cookie Policy & Technical Inventory
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('open-cookie-preferences'));
                    }
                  }}
                  className="text-[10px] font-bold text-teal-300 hover:text-teal-200 transition-all flex items-center gap-1.5 cursor-pointer text-left"
                >
                  <ShieldCheck size={11} className="text-teal-400" />
                  <span>Manage Cookie Preferences</span>
                </button>
              </li>
              <li>
                <a href="https://www.diamondnursingschool.com/contact" target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-slate-300 hover:text-white transition-all flex items-center gap-1.5">
                  Contact Compliance <ExternalLink size={9} className="text-slate-500" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Operational Path & Quick Action */}
        <div className="md:col-span-3 space-y-6">
           <div className={`p-6 rounded-3xl space-y-5 shadow-2xl relative overflow-hidden group border ${isClinicalTheme ? 'bg-slate-900/80 border-slate-700' : 'bg-slate-900/50 border-slate-800'}`}>
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:rotate-12 transition-transform duration-700 pointer-events-none">
                 <Zap size={60} />
              </div>
              <h4 className="text-[9px] font-black tracking-widest text-teal-300 flex items-center gap-2">
                 <Target size={12} className="text-amber-400" /> Operational Path
              </h4>
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white font-black text-xs shadow-inner ${isClinicalTheme ? 'bg-[#0F7D6C] border border-teal-500' : 'bg-indigo-600 border border-indigo-500'}`}>
                  {(activeCert?.shortName || "EDU").slice(0,3)}
                </div>
                <div className="min-w-0">
                  <p className="text-[11px] font-black text-white leading-tight truncate">
                    {activeCert?.name || "Clinical Certification"}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                    <p className="text-[8px] font-bold text-slate-300 tracking-tighter">50-State Board Aligned</p>
                  </div>
                </div>
              </div>

              {/* Direct CTA */}
              <a 
                href="https://www.diamondnursingschool.com/cna-career"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2.5 px-4 bg-teal-600 hover:bg-teal-500 text-white rounded-xl font-black text-[9px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 text-center block"
              >
                <span>Book CNA Class Now</span>
                <ExternalLink size={11} />
              </a>
           </div>
        </div>
      </div>

      <div className={`h-14 md:h-16 border-t px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4 ${isClinicalTheme ? 'bg-[#081F33] border-slate-800' : 'bg-black/40 border-slate-900'}`}>
        <div className="flex items-center gap-6">
          <p className="text-[9px] font-black text-slate-300 tracking-[0.2em]">
            &copy; {currentYear} Diamond School Of Nursing
          </p>
          <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-white/5 rounded-full border border-white/5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(16,185,129,0.4)]"></div>
            <span className="text-[8px] font-black text-emerald-300 tracking-widest">Protocol: Operational</span>
          </div>
        </div>

        <div className="flex items-center gap-8">
          <div className="hidden lg:flex items-center gap-6">
            <div className="flex items-center gap-1.5 group cursor-help">
              <span className="text-[8px] font-black tracking-tighter text-slate-400 group-hover:text-slate-200">Node: US-EAST-PRIMARY</span>
            </div>
            <div className="flex items-center gap-1.5 group cursor-help">
              <Server size={12} className="text-slate-400 group-hover:text-teal-300 transition-colors" />
              <span className="text-[8px] font-black tracking-tighter text-slate-400 group-hover:text-slate-200">Cluster: 08-GA</span>
            </div>
            <div className="flex items-center gap-1.5 group cursor-help">
              <Lock size={12} className="text-slate-600 group-hover:text-emerald-400 transition-colors" />
              <span className="text-[8px] font-black tracking-tighter text-slate-500 group-hover:text-slate-300">Secure Protocol Active</span>
            </div>
          </div>
          
          <div className="px-4 py-1.5 bg-indigo-600/10 border border-indigo-500/20 rounded-lg">
             <span className="text-[9px] font-black text-indigo-400 tracking-[0.3em]">Command Console</span>
          </div>
        </div>
      </div>

      <div className="bg-black/60 py-4 px-6 md:px-10 border-t border-slate-900/50">
        <Disclaimer />
      </div>
    </footer>
  );
};

